import {
  AnalyticsData,
  applyNow,
  buildPdpMeta,
  buildPropertyJsonLd,
  closeModal,
  deleteUndefined,
  FeesAnalyticsDataProvider,
  goBackToComponent,
  PdpMetaBuilderArgs,
  Property,
  seoHost,
  useAnalytics,
} from '@greystar/lib';
import { PropertyDetailsProps } from '@greystar/ui';
import {
  GetStaticComponentProps,
  RichText as JssRichText,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Head from 'next/head';
import router, { useRouter } from 'next/router';

import {
  ApplyInterstitialModal,
  HeaderTextBlock,
  Hero,
  LayoutContainer,
  MiniButton,
  MiniLink,
  PageSection,
  PageSectionContent,
} from '@greystar/designsystem';
import { useEffect, useState, JSX } from 'react';
import { personalize } from '@sitecore-cloudsdk/personalize/browser';
import useCloudSDK from '@greystar/ui/src/hooks/useCloudSDK';

type PersonalizeResponse = {
  contactUsCtaText?: string;
};

const PropertyDetails = (props: PropertyDetailsProps): JSX.Element => {
  const analytics = useAnalytics();
  const state = props?.data?.property?.location?.state ?? '';
  const propertyLocation = props?.data?.property?.location?.city ?? '';
  const propertyName = props?.data?.property?.name ?? '';
  const propertyId = props?.data?.property?.id ?? '';

  if (!props.data?.property) {
    return <></>;
  }

  const fullAddress = [
    props?.data?.property?.location?.address,
    props?.data?.property?.location?.city,
    props?.data?.property?.location?.state,
    props?.data?.property?.location?.postalCode,
  ]
    .filter(Boolean)
    .join(', ');

  const phoneNumber =
    props?.data?.property?.contact?.trackingPhoneNumber ??
    props?.data?.property?.contact?.phoneNumber ??
    '';

  const commonEventData = {
    action_type: '',
    state: state,
    property_location: propertyLocation,
    property_id: propertyId,
    property_name: propertyName,
    module_name: 'property details page',
    inpage_location: 'middle',
    click_text: '',
    click_type: 'internal',
    destination_url: '',
  };

  const handlePhoneClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const eventData = {
      ...commonEventData,
      action_type: 'call click',
      click_text: `tel:${phoneNumber}`,
      destination_url: `tel:${phoneNumber}`,
    };

    await analytics.trackEvent('property_call_click', eventData);

    router.push(`tel:${phoneNumber}`);
  };
  return (
    <PageSection isBottomFlushed className="md:pt-[64px]">
      <Head>
        <title>{props.data?.meta?.metaTitle}</title>
        <meta
          name="description"
          content={props.data?.meta?.metaDescription}
          data-testid="metaDescription"
        />
        <link
          data-testid="canonical"
          rel="canonical"
          href={`${seoHost}${props.data?.meta?.metaCanonicalLink}`}
        />
        <meta property="og:image" content={props.data?.meta?.metaOgImage} data-testid="ogImage" />
        <meta name="twitter:image" content={props.data?.meta?.metaOgImage} />
        <meta property="og:title" content={props.data?.meta?.metaOgTitle} data-testid="ogTitle" />
        <meta property="og:type" content={props.data?.meta?.metaOgType} data-testid="ogType" />
        <meta
          property="og:description"
          content={props.data?.meta?.metaOgDescription}
          data-testid="ogDescription"
        />
        <meta property="og:url" content={props.data?.meta?.metaOgUrl} data-testid="ogUrl" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@Greystar" />
        <meta name="twitter:creator" content="@Greystar" />
        <meta name="twitter:title" content={props.data?.meta?.metaOgTitle} />
        <meta name="twitter:description" content={props.data?.meta?.metaOgDescription} />
        <link
          rel="alternate"
          hrefLang="x-default"
          data-testid="x-default"
          href={`${seoHost}${props.data?.meta?.metaCanonicalLink}`}
        />
        {buildPropertyJsonLd(props.data.property)}
      </Head>
      <FeesAnalyticsDataProvider initialData={{ module_name: 'property details' }}>
        <PageSectionContent>
          <div className="mb-negative-half">
            <LayoutContainer flexDirection="column" paddingBottom="sm">
              <Hero hasPadding={false}>
                <Hero.HeaderTextBlock direction="reverse">
                  <HeaderTextBlock.Header header={props.data.property?.name} size="large" />
                  <HeaderTextBlock.Subtitle
                    variant="secondary"
                    subtitle={props.fields.subtitle?.value}
                  />
                </Hero.HeaderTextBlock>
                <Hero.AddressBar
                  address={fullAddress}
                  phoneNumber={phoneNumber}
                  href={`tel:${phoneNumber}`}
                  onClick={handlePhoneClick as unknown as () => void}
                />
                {((!props.data.hasAvailableUnits || !props.data.hasFees) && (
                  <Hero.BodyCopy>
                    <JssRichText field={props.fields.noPricingDisclaimer} />
                  </Hero.BodyCopy>
                )) || <></>}
                <Hero.Ctas>
                  <CtaRow data={props.data} commonEventData={commonEventData} />
                </Hero.Ctas>
              </Hero>
            </LayoutContainer>
          </div>
        </PageSectionContent>
      </FeesAnalyticsDataProvider>
    </PageSection>
  );
};

const CtaRow = ({
  data,
  commonEventData,
}: {
  data: PropertyDetailsProps['data'];
  commonEventData: AnalyticsData;
}) => {
  const analytics = useAnalytics();
  const { isPersonalizeEnabled } = useCloudSDK();
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const router = useRouter();
  const isCalculatorPage = router.asPath.includes('/calculator');
  const [btnText, setBtnText] = useState<string>('');
  const bookATourLink =
    data?.property?.contact?.communityWebsiteTrackingUrl &&
    data.property.contact.communityWebsiteTrackingUrl !== ''
      ? data.property.contact.communityWebsiteTrackingUrl
      : undefined;

  const handleButtonClick = async (
    eventName: string,
    clickText: string,
    clickType: 'internal' | 'external',
    destinationUrl: string,
    actionType?: string,
    event?: React.MouseEvent<HTMLAnchorElement>,
    isCalculatorPage?: boolean
  ) => {
    if (!data?.property) return;
    event?.preventDefault();

    const property = data.property;
    const eventData = {
      ...commonEventData,
      event: eventName,
      action_type: actionType ?? 'entry',
      click_text: clickText,
      click_type: clickType ?? 'internal',
      destination_url: destinationUrl ?? property?.contact?.leaseOnlineUrl,
    };
    await analytics.trackEvent(eventName, eventData);
    if (isCalculatorPage) {
      goBackToComponent(router, 'contact-us'); // Redirect to CDP page with anchor
    } else {
      if (eventName === 'property_contact_us_entry') {
        router.push(destinationUrl);
      } else {
        window.open(destinationUrl, '_blank');
      }
    }
  };

  const handleApplyClick = async () => {
    if (!data?.property?.contact?.leaseOnlineUrl) {
      await analytics.trackEvent('property_apply_entry', {
        ...commonEventData,
        action_type: 'entry',
        click_text: 'Apply',
        destination_url: data?.property?.contact?.leaseOnlineUrl ?? '',
      });
      goBackToComponent(router, 'contact-us');
    } else {
      setIsApplyModalOpen(true);

      const eventData = {
        ...commonEventData,
        action_type: 'interstitial',
        click_text: 'Apply',
        destination_url: '',
      };
      analytics.trackEvent(
        router.asPath.includes('calculator')
          ? 'unit_rent_calc_interstitial'
          : 'property_apply_interstitial',
        eventData
      );
    }
  };

  useEffect(() => {
    if (!isPersonalizeEnabled) {
      setBtnText('Contact Us');
      return;
    }

    const getPersonalizeResponse = async () => {
      try {
        const personalizeResponse = await personalize({
          channel: 'WEB',
          currency: 'USD',
          friendlyId: 'contact_us_cta',
        });
        const res = personalizeResponse as PersonalizeResponse;
        const personalizedBtnText = res?.contactUsCtaText ?? 'Contact Us';
        setBtnText(personalizedBtnText);
        analytics.trackExperimentEvent({
          experimentId: 'contact_us_cta',
          variantName: personalizedBtnText,
        });
      } catch (error) {
        console.debug('CloudSDK personalize not initialized, using default CTA text:', error);
        setBtnText('Contact Us');
      }
    };

    getPersonalizeResponse();
  }, [isPersonalizeEnabled, analytics]);

  if (!btnText) return <></>;

  return (
    <>
      <MiniButton
        label="Apply"
        variant="primary"
        icon={
          data?.property?.contact?.leaseOnlineUrl
            ? { position: 'right', name: 'ExternalLink' }
            : undefined
        }
        isDisabled={false}
        onClick={handleApplyClick}
      />

      {bookATourLink && (
        <MiniLink
          label="Visit Property Website"
          variant="tertiary"
          href={bookATourLink}
          icon={{ position: 'right', name: 'ExternalLink' }}
          onClick={() => {
            handleButtonClick(
              'property_book_virtual_tour_entry',
              'Visit Property Website',
              'internal',
              isCalculatorPage ? '' : bookATourLink
            );
          }}
          {...{ target: '_blank', rel: 'noopener noreferrer' }}
        />
      )}

      <MiniLink
        label={btnText}
        variant="tertiary"
        href={'#contact-us'} // Use undefined href for router navigation
        onClick={() => {
          handleButtonClick(
            'property_contact_us_entry',
            btnText,
            'internal',
            isCalculatorPage ? '' : '#contact-us',
            undefined,
            undefined,
            isCalculatorPage
          );
        }}
      />
      <MiniLink
        label="Resident Portal"
        variant="tertiary"
        href={data?.property?.contact?.residentPortalUrl}
        onClick={() =>
          handleButtonClick(
            'property_resident_portal_entry',
            'Resident Portal',
            'external',
            data?.property?.contact?.residentPortalUrl || ''
          )
        }
        icon={
          data?.property?.contact?.residentPortalUrl
            ? { position: 'right', name: 'ExternalLink' }
            : undefined
        }
        {...(data?.property?.contact?.residentPortalUrl
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      />
      {isApplyModalOpen && (
        <ApplyInterstitialModal
          isOpen={isApplyModalOpen}
          onClose={() => {
            closeModal({
              setIsModalOpen: setIsApplyModalOpen,
              trackEvent: analytics.trackEvent,
              eventData: {
                ...commonEventData,
              },
              applyNowUrl: data?.property?.contact?.leaseOnlineUrl,
              eventName: router.asPath.includes('calculator')
                ? 'unit_rent_calc_interstitial_close'
                : 'property_apply_interstitial_close',
            });
          }}
          onAccept={() => {
            applyNow({
              router,
              setIsModalOpen: setIsApplyModalOpen,
              applyNowUrl: data?.property?.contact?.leaseOnlineUrl,
              trackEvent: analytics.trackEvent,
              eventName: router.asPath.includes('calculator')
                ? 'unit_rent_calc_apply'
                : 'property_apply_entry',
              eventData: {
                ...commonEventData,
                action_type: 'entry',
                click_text: 'Continue',
                destination_url: data?.property?.contact?.leaseOnlineUrl ?? '',
              },
            });
          }}
        />
      )}
    </>
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering,
  layoutData
): Promise<PropertyDetailsProps> => {
  const props = rendering as unknown as PropertyDetailsProps;
  const property = layoutData.sitecore.context.property as Property;

  if (!property) {
    return {
      ...props,
      data: {
        property: null,
        meta: null,
        hasAvailableUnits: false,
        hasFees: false,
        notFound: true,
      },
    };
  }

  const metaArgs: PdpMetaBuilderArgs | null = props.fields
    ? {
        metaTitleTemplate: props.fields.metaTitleTemplate?.value,
        metaDescriptionTemplate: props.fields.metaDescriptionTemplate?.value,
        ogTitleTemplate: props.fields.ogTitleTemplate?.value,
        ogType: props.fields.ogType?.value,
        ogDescriptionTemplate: props.fields.ogDescriptionTemplate?.value,
      }
    : null;

  const meta = metaArgs ? await buildPdpMeta(property, metaArgs) : null;

  return {
    ...props,
    data: {
      property: deleteUndefined<Property>({
        id: property.id,
        name: property.name,
        about: property.about,
        location: property.location,
        contact: property.contact,
        highlights: property.highlights,
        floorplans: property.floorplans,
        imageGallery: property.imageGallery,
        availableUnits: [],
        tours: property.tours ?? [],
      }),
      hasAvailableUnits: !!property.availableUnits?.length,
      hasFees: !!property.fees?.length,
      meta,
      notFound: false,
    },
  };
};

export default withDatasourceCheck()<PropertyDetailsProps>(PropertyDetails);
